/**
 * SUN BLIND SPOT — the Realism Panel toggle that hides a contact when it sits
 * near the sun. (REAL NAVIGATION, the panel's other unconsumed toggle, is a
 * much smaller change — `mission-hud.ts`'s `missionHudFrom` takes an option to
 * drop the waypoint cue — and lives there instead.)
 *
 * The original's mechanism is recovered by static RE of `RB.EXE`'s `sun.c`
 * (module located the AD-010 way: the `s\game\sun.c` `__FILE__` string's own
 * segment, `0x4012`, cross-referenced against the MZ relocation table to find
 * the two `assert()` call sites that push it, landing the routine at file
 * `0xfa48`+, load base `0x4800`). The routine samples nine points around the
 * target against a fixed screen-space radius (`0xc8` = 200 units) centred on
 * the sun's projected position, gated by the live flag word at `ds:0x7269`
 * (`docs/formats/missions-campaign.md`'s `CREAL.PRF`/`MREAL.PRF` field map —
 * SUN is option index 1 of 13, `ds:0x7267 + 2`). Full evidence trail:
 * `docs/engineering/sun-blind-spot-design.md`.
 *
 * That is a *screen-space pixel* test against the original's own viewport and
 * field of view, neither of which survived into a form this port can replay
 * bit-for-bit. What ports cleanly is the shape: a fixed angular cone around
 * the sun's direction, tested against the contact's true bearing rather than
 * a rendered position, so it works the same whether the contact is drawn by
 * the WebGPU scene or only named on the HUD arrow. `SUN_BLIND_SPOT_HALF_ANGLE_DEG`
 * is therefore CALIBRATION-PENDING: a plausible cone, not the recovered one —
 * recovering the true angle needs the original's FOV/viewport constants,
 * which have not been found, or a live dosbox-x measurement, neither of which
 * this pass had time for.
 *
 * One nuance is *not* reproduced: the manual's "not visible until very close"
 * is an emergent property of the original's fixed-*pixel* radius against a
 * target whose *screen* footprint grows as it closes — nothing here shrinks
 * with range, so a contact stays hidden at any distance while inside the
 * cone. Documented rather than faked with an invented falloff constant.
 */

import { dotVec3, lengthVec3, type TimeOfDay, type Vec3 } from "@red-opus/engine";
import type { CombatHud } from "./combat-hud.ts";
import type { AircraftView } from "./encounter-views.ts";
import { sunDirectionAt } from "./time-of-day.ts";

/**
 * CALIBRATION-PENDING (see file doc). Chosen to read like "the sun is right
 * there, you cannot look at it" without swallowing half the sky — a WWI
 * pilot's blind spot, not a targeting reticle.
 */
export const SUN_BLIND_SPOT_HALF_ANGLE_DEG = 12;

/**
 * The sun's direction from `MissionWeather.timeOfDay` lives in `time-of-day.ts`
 * as `sunDirectionAt`, alongside the disc that gets drawn at that bearing —
 * **one sun**, so the cone tested here and the disc in the sky cannot disagree
 * (`time-of-day.test.ts` pins that as an identity across all four buckets).
 *
 * Re-exported under the name this module has always used. Three things about it
 * matter here: it is `sun.c`'s own recovered law rather than the three
 * hand-picked vectors that used to sit in this file; it is `undefined` at night
 * because the sim's day/night test is exactly `sin(hourAngle) < 0`, so there is
 * no sun above the horizon to fly into; and at midday it points overhead, which
 * means the toggle mostly does nothing then — as it mostly does nothing in
 * bright overhead light for a real pilot too.
 */
export { sunDirectionAt as sunDirectionFor } from "./time-of-day.ts";

/** Angle between two direction vectors, in degrees — 180 when either is degenerate. */
export function angleBetweenDeg(a: Vec3, b: Vec3): number {
  const denom = lengthVec3(a) * lengthVec3(b);
  if (denom <= 0) return 180;
  const cosine = Math.min(1, Math.max(-1, dotVec3(a, b) / denom));
  return (Math.acos(cosine) * 180) / Math.PI;
}

/**
 * True when `target` sits inside the blind-spot cone as seen from `observer`
 * — a pure bearing test, independent of which way the observer's nose is
 * pointed (the original's own screen-space test only ever fires when the sun
 * is already in view, but this port's HUD arrow has no "off camera" concept
 * to hang that on; see the file doc).
 */
export function isInSunBlindSpot(observer: Vec3, target: Vec3, sunDirection: Vec3 | undefined): boolean {
  if (!sunDirection) return false;
  const toTarget: Vec3 = { x: target.x - observer.x, y: target.y - observer.y, z: target.z - observer.z };
  if (lengthVec3(toTarget) === 0) return false;
  return angleBetweenDeg(toTarget, sunDirection) <= SUN_BLIND_SPOT_HALF_ANGLE_DEG;
}

/** What a frame needs to run the suppression test: on/off, and the sun's direction (or none). */
export interface SunSuppression {
  readonly enabled: boolean;
  readonly direction: Vec3 | undefined;
}

export const SUN_BLIND_SPOT_OFF: SunSuppression = { enabled: false, direction: undefined };

/** Builds the frame's suppression state once, from the panel toggle and the mission's briefed time of day. */
export function sunSuppressionFor(sunBlindSpotOn: boolean, timeOfDay: TimeOfDay | undefined): SunSuppression {
  return sunBlindSpotOn ? { enabled: true, direction: sunDirectionAt(timeOfDay) } : SUN_BLIND_SPOT_OFF;
}

/** The ids of every aircraft in `fleet` that the sun currently hides from `observer`. */
export function sunSuppressedIds(
  observer: Vec3,
  fleet: readonly { readonly id: string; readonly position: Vec3 }[],
  sun: SunSuppression,
): ReadonlySet<string> {
  if (!sun.enabled || !sun.direction) return EMPTY_IDS;
  const ids = new Set<string>();
  for (const entry of fleet) {
    if (isInSunBlindSpot(observer, entry.position, sun.direction)) ids.add(entry.id);
  }
  return ids;
}

const EMPTY_IDS: ReadonlySet<string> = new Set();

/** What `flight-scene.ts`'s frame loop does with `sunVisibleFleet`'s result. */
export interface SunFleetFilter {
  /** `others`, with every sun-hidden id dropped — `setFleet` already treats a missing id as invisible. */
  readonly fleet: readonly AircraftView[];
  readonly hiddenIds: ReadonlySet<string>;
}

/**
 * The renderer's whole share of SUN BLIND SPOT: drop a hidden aeroplane from
 * the list handed to `combat-visuals.ts`'s `setFleet`, which already treats
 * "not in this frame's list" as invisible (`hideMissing`) — so this is the
 * entire suppression of the 3D model, with no change to the sim's own roster
 * or targeting. Returns the same array when nothing is hidden, so a flight
 * with the toggle off allocates nothing extra.
 */
export function sunVisibleFleet(observer: Vec3, fleet: readonly AircraftView[], sun: SunSuppression): SunFleetFilter {
  const hiddenIds = sunSuppressedIds(
    observer,
    fleet.map((view) => ({ id: view.id, position: view.pose.position })),
    sun,
  );
  return { fleet: hiddenIds.size > 0 ? fleet.filter((view) => !hiddenIds.has(view.id)) : fleet, hiddenIds };
}

/**
 * The HUD's share: the same `hiddenIds` `sunVisibleFleet` just named, applied
 * to the contact arrow. Target *selection* (`combat-hud.ts`'s `trackedEnemy`)
 * is untouched — only whether this frame reports the pick.
 */
export function sunSuppressedCombat(combat: CombatHud, hiddenIds: ReadonlySet<string>): CombatHud {
  return combat.enemy && hiddenIds.has(combat.enemy.id) ? { ...combat, enemy: undefined } : combat;
}
